import {
    ConfigurationItemArrayEditor,
    TagSelectInput,
} from '@client/components/_core/configuration/ConfigurationItemArrayEditor';
import { PageController } from '@client/components/_core/layout/PageController';
import { LearnedDecisionsCard } from '@client/components/_shared/auto-detect/LearnedDecisionsCard';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useConfigServiceLoader } from '@client/hooks/business/configuration/useConfigServiceLoader';
import { trpc } from '@client/trpc/client';
import { VULNERABILITY_TYPE_KEYS, VULNERABILITY_TYPE_METADATA } from '@shared/types';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Checkbox, Form, Select, Switch, Typography } from 'antd';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Inbox } from 'lucide-react';
import { useState } from 'react';

/**
 * Auto-detect classifier configuration. Org admins control four user-facing
 * knobs:
 *
 *   1. Pre-filter skip categories — checkbox list of MessageClassificationSkipCategory
 *      values the noise filter is allowed to act on.
 *   2. Sender pre-filter allowlist — emails/domains/phones that bypass the
 *      pre-filter skip rules (still subject to dedup).
 *   3. Sender blocklist — emails/domains/phones auto-archived to NO_ISSUE.
 *   4. Custom complaint cues — org-specific subject regex + body phrases that
 *      AUGMENT the system COMPLAINT_SUBJECT_PATTERNS.
 *
 * Three platform-tuning settings (duplicate lookback window, semantic cache
 * override, tier downgrade override) are intentionally NOT exposed here —
 * those are implementation knobs that should stay platform-admin-managed,
 * not org-level. They remain in the ConfigurationItem registry so they
 * still drive runtime behaviour.
 */
export const Route = createFileRoute('/console/$orgId/configuration/compliance/auto-detect')({
    staticData: {
        metadata: {
            title: 'Auto-detect',
        },
        ui: {
            icon: Inbox,
            title: 'Auto-detect configuration',
            subtitle: 'Configure noise filters, sender lists, and detection cues for the AI classifier',
            menuDescription:
                'Configure noise filters, sender allow and block lists, and detection cues for the AI classifier.',
        },
        modules: ['auto-detect'],
        permissions: ['settings:update'],
    },
    component: AutoDetectConfiguration,
});

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;
const DOMAIN_PATTERN = /^[a-z0-9.-]+\.[a-z]{2,}$/i;
const PHONE_LIKE_PATTERN = /^[+\d][\d\s\-()]{4,18}$/;

const SENDER_RULES = [
    {
        async validator(_: unknown, value: string) {
            if (!value?.trim()) throw new Error('Entry cannot be empty');
            const trimmed = value.trim();

            if (EMAIL_PATTERN.test(trimmed) || DOMAIN_PATTERN.test(trimmed)) return;

            // Phone-shaped: validate via libphonenumber so we reject digits-
            // and-separators junk that would canonicalise to nothing on the
            // server and silently never match. Mirrors the server-side
            // `parseSenderRuleSet` behaviour so the UI doesn't accept entries
            // the server will drop.
            if (PHONE_LIKE_PATTERN.test(trimmed)) {
                const phone = parsePhoneNumberFromString(trimmed, 'AU');
                if (!phone?.isValid()) {
                    throw new Error(
                        'Phone number is not valid. Use E.164 (+61400000001) or local format (0400 000 001).',
                    );
                }
                return;
            }

            throw new Error('Must be an email (a@b.com), bare domain (broker.example.com), or phone number');
        },
    },
];

function AutoDetectConfiguration() {
    return (
        <PageController route={Route}>
            <Space direction="vertical" size="large" className="w-full">
                <SkipCategoriesSection />

                <ConfigurationItemArrayEditor
                    title="Sender pre-filter allowlist"
                    settingToEdit="AUTO_DETECT_SENDER_ALLOWLIST"
                    description="Emails (broker@example.com), bare domains (broker.example.com), or phone numbers (+61400000001) that bypass the rule-based pre-filter so their messages always reach the AI classifier. Duplicate detection still applies."
                    itemRules={SENDER_RULES}
                    tagsModePlaceholder="broker@example.com, broker.example.com, +61400000001"
                    tagsModeTokenSeparators={[',', '\n']}
                />

                <ConfigurationItemArrayEditor
                    title="Sender blocklist"
                    settingToEdit="AUTO_DETECT_SENDER_BLOCKLIST"
                    description="Emails (marketing@partner.com), bare domains (newsletter.example.com), or phone numbers (+61400000001) that are auto-archived as NO_ISSUE without classification. The original message and any attachments are preserved — the AI is simply not invoked. Use sparingly; an allowlist entry is always safer than a blocklist entry."
                    itemRules={SENDER_RULES}
                    tagsModePlaceholder="marketing@partner.com, newsletter.example.com"
                    tagsModeTokenSeparators={[',', '\n']}
                />

                <ConfigurationItemArrayEditor
                    title="Internal email domains"
                    settingToEdit="AUTO_DETECT_INTERNAL_EMAIL_DOMAINS"
                    description="Bare domains (no @ or path) the AI should treat as internal staff senders — typically your own brand plus any subsidiaries or acquired-brand domains. Used to derive message direction (INBOUND / OUTBOUND / INTERNAL) so the AI can calibrate Cat 3 standing and Cat 7 abrupt-tone signals appropriately. Leave empty to auto-default to your verified domain. Subdomain matches are recognised automatically (sg.acme.com matches acme.com)."
                    itemRules={INTERNAL_DOMAIN_RULES}
                    deleteOrgOverrideWhenEmpty
                    tagsModePlaceholder="acme.com, brand.acme.com"
                    tagsModeTokenSeparators={[',', '\n']}
                />

                <ComplaintCuesSection />

                <LearnedDecisionsCard managed />

                <VulnerabilityDetectionSection />
            </Space>
        </PageController>
    );
}

// ─── Vulnerability detection (D6) ────────────────────────────────────────

function VulnerabilityDetectionSection() {
    const [enabled, setEnabled] = useState<boolean | null>(null);
    const [enabledTypes, setEnabledTypes] = useState<string[] | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const { mutateAsync: upsert } = trpc.configurationItem.upsertConfigurationItemOrg.useMutation();
    const { mutateAsync: del } = trpc.configurationItem.deleteConfigurationItemOrg.useMutation();

    const { data: enabledConfig, refetch: refetchEnabled } = useConfigServiceLoader(async (configService) => {
        const configuration = await configService.fetchConfiguration('AUTO_DETECT_VULNERABILITY_DETECTION_ENABLED', {
            staleTime: 'immediate',
        });
        try {
            const parsed = JSON.parse(configuration.value || 'null');
            setEnabled(parsed !== false);
        } catch {
            setEnabled(true);
        }
        return configuration;
    });

    const { data: typesConfig, refetch: refetchTypes } = useConfigServiceLoader(async (configService) => {
        const configuration = await configService.fetchConfiguration('AUTO_DETECT_VULNERABILITY_TYPES_ENABLED', {
            staleTime: 'immediate',
        });
        try {
            const parsed = JSON.parse(configuration.value || 'null');
            setEnabledTypes(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : null);
        } catch {
            setEnabledTypes(null);
        }
        return configuration;
    });

    const handleToggleEnabled = async (next: boolean) => {
        setIsSaving(true);
        try {
            message.loading('Saving…');
            await upsert({
                name: 'AUTO_DETECT_VULNERABILITY_DETECTION_ENABLED',
                value: JSON.stringify(next),
            });
            setEnabled(next);
            message.success(next ? 'Vulnerability detection enabled.' : 'Vulnerability detection disabled.');
            void refetchEnabled();
        } catch (e) {
            errorMessage.show(e);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveTypes = async () => {
        setIsSaving(true);
        try {
            message.loading('Saving…');
            if (enabledTypes === null || enabledTypes.length === VULNERABILITY_TYPE_KEYS.length) {
                // null = "all enabled" — drop the per-org overlay to fall back to that default.
                await del({ name: 'AUTO_DETECT_VULNERABILITY_TYPES_ENABLED' });
            } else {
                await upsert({
                    name: 'AUTO_DETECT_VULNERABILITY_TYPES_ENABLED',
                    value: JSON.stringify(enabledTypes),
                });
            }
            message.success('Saved.');
            void refetchTypes();
        } catch (e) {
            errorMessage.show(e);
        } finally {
            setIsSaving(false);
        }
    };

    const typeOptions = VULNERABILITY_TYPE_KEYS.map((key) => {
        const meta = VULNERABILITY_TYPE_METADATA[key];
        return {
            value: key,
            label: `${meta.label}${meta.isSensitive ? ' (sensitive)' : ''}`,
        };
    });

    const isDisabled = enabled === false;

    return (
        <Card title="Vulnerability detection" size="small" loading={!enabledConfig || !typesConfig}>
            <Typography.Paragraph type="secondary" className="mb-3">
                The classifier flags 17 vulnerability signals aligned with GICOP duty-of-care obligations (e.g.
                bereavement, language barrier, family violence). Detections appear in the auto-detect inbox alongside
                the issue type and can be promoted into persistent contact-level flags.
            </Typography.Paragraph>
            <Space direction="vertical" size="middle" className="w-full">
                <div className="flex items-center gap-3">
                    <Switch
                        checked={enabled !== false}
                        onChange={handleToggleEnabled}
                        disabled={isSaving || enabled === null}
                    />
                    <Typography.Text>{enabled === false ? 'Disabled' : 'Enabled'}</Typography.Text>
                </div>
                <div>
                    <Typography.Text strong className="mb-1 block">
                        Detected vulnerability types
                    </Typography.Text>
                    <Typography.Paragraph type="secondary" className="mb-2 text-xs">
                        Leave empty to detect all 17 types (default). Select a subset to limit detection — useful for
                        orgs without a dedicated response policy for certain categories. The model is always shown the
                        full type roster for calibration; disabled types are stripped server-side.
                    </Typography.Paragraph>
                    <Select
                        mode="multiple"
                        className="w-full"
                        placeholder="All types enabled (default)"
                        value={enabledTypes ?? []}
                        onChange={(values) => setEnabledTypes(values.length === 0 ? null : values)}
                        options={typeOptions}
                        disabled={isSaving || isDisabled}
                    />
                </div>
                <Space className="flex justify-end">
                    <Button type="primary" onClick={handleSaveTypes} disabled={isSaving} loading={isSaving}>
                        Save
                    </Button>
                </Space>
            </Space>
        </Card>
    );
}

const INTERNAL_DOMAIN_RULES = [
    {
        async validator(_: unknown, value: string) {
            if (!value?.trim()) throw new Error('Domain cannot be empty');
            const trimmed = value.trim().toLowerCase();
            if (trimmed.includes('@')) {
                throw new Error('Enter a bare domain (e.g. brokerco.com), not a full email address');
            }
            if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(trimmed)) {
                throw new Error('Must be a bare domain like brokerco.com or brand.brokerco.com');
            }
        },
    },
];

// ─── Skip categories — checkbox list ──────────────────────────────────────

const SKIP_CATEGORY_OPTIONS: ReadonlyArray<{ value: string; label: string; help: string }> = [
    { value: 'AUTO_REPLY', label: 'Automatic replies', help: 'Out-of-office responses and auto-generated replies.' },
    { value: 'NEWSLETTER', label: 'Newsletters', help: 'Subscription emails and mailing-list content.' },
    {
        value: 'DELIVERY_FAILURE',
        label: 'Delivery failures',
        help: 'Bounce notifications and undeliverable-mail messages.',
    },
    {
        value: 'OUT_OF_OFFICE',
        label: 'Out-of-office',
        help: 'Vacation / annual leave responses (also caught by Automatic replies — leave both checked).',
    },
    {
        value: 'SPAM_PATTERN',
        label: 'Spam patterns',
        help: 'Messages matching known spam heuristics.',
    },
    {
        value: 'MARKETING',
        label: 'Marketing',
        help: 'Promotional content and call-to-action messages.',
    },
];

function SkipCategoriesSection() {
    const [selected, setSelected] = useState<string[] | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const { mutateAsync: upsert } = trpc.configurationItem.upsertConfigurationItemOrg.useMutation();
    const { mutateAsync: del } = trpc.configurationItem.deleteConfigurationItemOrg.useMutation();

    const { data, isLoading, refetch } = useConfigServiceLoader(async (configService) => {
        const configuration = await configService.fetchConfiguration('AUTO_DETECT_SKIP_CATEGORIES', {
            staleTime: 'immediate',
        });
        try {
            const parsed = JSON.parse(configuration.value || '[]');
            setSelected(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
        } catch {
            setSelected([]);
        }
        return configuration;
    });

    const handleSave = async () => {
        if (selected === null) return;
        setIsSaving(true);
        try {
            message.loading('Saving…');
            await upsert({
                name: 'AUTO_DETECT_SKIP_CATEGORIES',
                value: JSON.stringify(selected),
            });
            message.success('Saved.');
            void refetch();
        } catch (e) {
            errorMessage.show(e);
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = async () => {
        setIsSaving(true);
        try {
            message.loading('Resetting to system default…');
            await del({ name: 'AUTO_DETECT_SKIP_CATEGORIES' });
            message.success('Reset to default.');
            void refetch();
        } catch (e) {
            errorMessage.show(e);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Card title="Pre-filter skip categories" size="small" loading={isLoading}>
            <Typography.Paragraph type="secondary" className="mb-3">
                Categories the rule-based pre-filter is allowed to skip without invoking the AI. Unchecking a category
                means messages matching that category will instead be classified by the AI. The default covers everyday
                noise (auto-replies, newsletters, delivery failures, marketing).
            </Typography.Paragraph>
            <Space direction="vertical" size="small" className="w-full">
                <Checkbox.Group
                    value={selected ?? []}
                    onChange={(values) => setSelected(values as string[])}
                    disabled={isSaving || isLoading}
                >
                    <Space direction="vertical" size="small">
                        {SKIP_CATEGORY_OPTIONS.map((option) => (
                            <div key={option.value}>
                                <Checkbox value={option.value}>{option.label}</Checkbox>
                                <Typography.Paragraph type="secondary" className="mb-0! ml-6 text-xs">
                                    {option.help}
                                </Typography.Paragraph>
                            </div>
                        ))}
                    </Space>
                </Checkbox.Group>
                <Space className="mt-3 flex justify-end">
                    <Button onClick={handleReset} disabled={!data || isSaving} loading={isSaving}>
                        Reset to default
                    </Button>
                    <Button type="primary" onClick={handleSave} disabled={!data || isSaving} loading={isSaving}>
                        Save
                    </Button>
                </Space>
            </Space>
        </Card>
    );
}

// ─── Complaint cues — two list editors in one card ───────────────────────

const SUBJECT_REGEX_RULES = [
    {
        async validator(_: unknown, value: string) {
            if (!value?.trim()) throw new Error('Pattern cannot be empty');
            if (value.length > 200) throw new Error('Pattern is too long (max 200 characters)');
            try {
                new RegExp(value, 'i');
            } catch {
                throw new Error('Invalid regular expression');
            }
        },
    },
];

const BODY_PHRASE_RULES = [
    {
        async validator(_: unknown, value: string) {
            if (!value?.trim()) throw new Error('Phrase cannot be empty');
            const trimmed = value.trim();
            if (trimmed.length < 4) throw new Error('Phrase must be at least 4 characters');
            if (trimmed.length > 100) throw new Error('Phrase is too long (max 100 characters)');
        },
    },
];

type ComplaintCuesValue = { subject: string[]; body: string[] };

function ComplaintCuesSection() {
    const [form] = Form.useForm<ComplaintCuesValue>();
    const [isSaving, setIsSaving] = useState(false);
    const { mutateAsync: upsert } = trpc.configurationItem.upsertConfigurationItemOrg.useMutation();
    const { mutateAsync: del } = trpc.configurationItem.deleteConfigurationItemOrg.useMutation();

    const { data, isLoading, refetch } = useConfigServiceLoader(async (configService) => {
        const configuration = await configService.fetchConfiguration('AUTO_DETECT_EXTRA_COMPLAINT_PATTERNS', {
            staleTime: 'immediate',
        });
        let parsed: ComplaintCuesValue = { subject: [], body: [] };
        try {
            const obj = JSON.parse(configuration.value || '{}');
            parsed = {
                subject: Array.isArray(obj?.subject) ? obj.subject.filter((v: unknown) => typeof v === 'string') : [],
                body: Array.isArray(obj?.body) ? obj.body.filter((v: unknown) => typeof v === 'string') : [],
            };
        } catch {
            // fall through with defaults
        }
        form.setFieldsValue(parsed);
        return configuration;
    });

    const handleSave = async (values: ComplaintCuesValue) => {
        setIsSaving(true);
        try {
            message.loading('Saving…');
            await upsert({
                name: 'AUTO_DETECT_EXTRA_COMPLAINT_PATTERNS',
                value: JSON.stringify({
                    subject: values.subject ?? [],
                    body: values.body ?? [],
                }),
            });
            message.success('Saved.');
            void refetch();
        } catch (e) {
            errorMessage.show(e);
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = async () => {
        setIsSaving(true);
        try {
            message.loading('Resetting to system default…');
            await del({ name: 'AUTO_DETECT_EXTRA_COMPLAINT_PATTERNS' });
            message.success('Reset to default.');
            void refetch();
        } catch (e) {
            errorMessage.show(e);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Card title="Custom complaint cues" size="small" loading={isLoading}>
            <Typography.Paragraph type="secondary" className="mb-3">
                Org-specific complaint signals on top of the system patterns. Use when customers consistently use
                language the default model misses (e.g. brand-specific product names or in-house jargon).
            </Typography.Paragraph>
            <Form form={form} onFinish={handleSave} layout="vertical" disabled={isSaving || isLoading}>
                <Typography.Title level={5} className="mt-0! mb-1!">
                    Subject patterns (regular expressions)
                </Typography.Title>
                <Typography.Paragraph type="secondary" className="mb-2! text-xs">
                    Case-insensitive regex matched against the email subject. Patterns with nested quantifiers (like{' '}
                    <code>(.+)+</code> or <code>(a*)*</code>) are rejected on save.
                </Typography.Paragraph>
                <Form.Item name="subject" className="mb-3!">
                    <TagSelectInput
                        itemRules={SUBJECT_REGEX_RULES}
                        protectedItems={[]}
                        placeholder="^Re:\s*Refund"
                        tokenSeparators={['\n']}
                        aria-label="Subject patterns"
                    />
                </Form.Item>

                <Typography.Title level={5} className="mt-3! mb-1!">
                    Body phrases (literal text)
                </Typography.Title>
                <Typography.Paragraph type="secondary" className="mb-2! text-xs">
                    Literal phrases matched anywhere in the message body (case-insensitive substring). Not regex — enter
                    the exact wording you want to match.
                </Typography.Paragraph>
                <Form.Item name="body" className="mb-3!">
                    <TagSelectInput
                        itemRules={BODY_PHRASE_RULES}
                        protectedItems={[]}
                        placeholder="demand immediate refund"
                        tokenSeparators={['\n']}
                        aria-label="Body phrases"
                    />
                </Form.Item>

                <Space className="mt-3 flex justify-end">
                    <Button onClick={handleReset} disabled={!data || isSaving} loading={isSaving}>
                        Reset to default
                    </Button>
                    <Button type="primary" htmlType="submit" disabled={!data || isSaving} loading={isSaving}>
                        Save
                    </Button>
                </Space>
            </Form>
        </Card>
    );
}
